import { Field } from "@base-ui/react/field"
import { Select as BaseSelect } from "@base-ui/react/select"
import { css } from "dx-styles"
import {
  Children,
  isValidElement,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react"

import { tokens } from "../theme"
import { controlSurface } from "./control"
import { fieldLabel, FieldMessage } from "./Field"
import { popupItem, popupPositioner, popupSurface } from "./popup"

const root = css({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.xs,
})

const trigger = css(controlSurface, {
  justifyContent: "space-between",
  gap: tokens.space.sm,
  cursor: "pointer",
  textAlign: "start",
})

const valueText = css({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
})

const placeholderText = css({
  color: tokens.color.textDescription,
})

const triggerIcon = css({
  display: "inline-flex",
  flexShrink: 0,
  color: tokens.color.textDescription,
})

const itemIndicator = css({
  display: "inline-flex",
  flexShrink: 0,
  color: tokens.color.primary,
})

const ChevronIcon = () => {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path
        d="M1 3.5L5 7.5L9 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const CheckIcon = () => {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path
        d="M1 5.5L3.5 8L9 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Values a select can carry. Narrow on purpose — these serialise predictably. */
export type SelectValue = string | number

export type SelectProps<TValue extends SelectValue> = {
  /** Required — an unlabelled control is an accessibility failure. */
  label: string
  /** Hint shown under the control. Replaced by `error` when there is one. */
  description?: string
  /** When set, the field renders invalid and this replaces `description`. */
  error?: string
  /** Shown until something is chosen. */
  placeholder?: string
  value?: TValue
  defaultValue?: TValue
  onValueChange?: (value: TValue) => void
  disabled?: boolean
  required?: boolean
  /** Identifies the field when a form is submitted. */
  name?: string
  children: ReactNode
}

export type SelectItemProps<TValue extends SelectValue> = {
  value: TValue
  disabled?: boolean
  children: ReactNode
}

/**
 * Declared before `SelectImpl`, which compares `child.type` against it: an arrow
 * function is not hoisted, so the other order leaves the reference in the
 * temporal dead zone.
 */
const SelectItemImpl = <TValue extends SelectValue>({
  value,
  disabled,
  children,
}: SelectItemProps<TValue>): ReactElement => {
  return (
    <BaseSelect.Item
      className={popupItem}
      value={value}
      disabled={disabled}
    >
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
      <BaseSelect.ItemIndicator className={itemIndicator}>
        <CheckIcon />
      </BaseSelect.ItemIndicator>
    </BaseSelect.Item>
  )
}

const SelectImpl = <TValue extends SelectValue>({
  label: labelText,
  description,
  error,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  disabled,
  required,
  name,
  children,
}: SelectProps<TValue>): ReactElement => {
  const isControlled = value !== undefined
  const [uncontrolledValue, setUncontrolledValue] = useState<TValue | null>(
    defaultValue ?? null,
  )
  const current = isControlled ? value : uncontrolledValue
  const showPlaceholder = current === null

  /**
   * Base UI resolves the trigger's text from `Root`'s `items`, not from mounted
   * `Select.Item`s — and the items live in a portal that stays unmounted until
   * the popup opens. Without this the trigger would show the raw value ("pro"
   * rather than "Pro"). Reading the labels off the children keeps the compound
   * API intact instead of making callers pass a parallel `items` array.
   *
   * Only direct `SelectItem` children are picked up, which is all this API
   * allows — there is no `Group` part.
   */
  const items = useMemo(
    () =>
      // oxlint-disable-next-line react/no-react-children
      Children.toArray(children)
        .filter(
          (child): child is ReactElement<SelectItemProps<TValue>> =>
            isValidElement(child) && child.type === SelectItemImpl,
        )
        .map((child) => ({
          label: child.props.children,
          value: child.props.value,
        })),
    [children],
  )

  return (
    <Field.Root
      className={root}
      invalid={Boolean(error)}
      disabled={disabled}
    >
      <Field.Label className={fieldLabel}>{labelText}</Field.Label>

      <BaseSelect.Root
        items={items}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        required={required}
        name={name}
        onValueChange={(next) => {
          // Base UI types the single-select callback as `TValue | null`, but a
          // non-clearable select never emits null once an item is chosen. Guard
          // rather than cast, so the narrower public signature stays honest.
          if (next === null) return
          if (!isControlled) setUncontrolledValue(next)
          onValueChange?.(next)
        }}
      >
        <BaseSelect.Trigger className={trigger}>
          {showPlaceholder ? (
            <span className={placeholderText}>{placeholder}</span>
          ) : (
            <BaseSelect.Value className={valueText} />
          )}
          <BaseSelect.Icon className={triggerIcon}>
            <ChevronIcon />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>

        <BaseSelect.Portal>
          <BaseSelect.Positioner
            className={popupPositioner}
            sideOffset={4}
          >
            <BaseSelect.Popup className={popupSurface}>
              {children}
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>

      <FieldMessage
        description={description}
        error={error}
      />
    </Field.Root>
  )
}

/**
 * Builds a `Select` and its `SelectItem` bound to one value type.
 *
 * JSX children do not inherit a parent's type parameter, so a plain compound
 * `<Select>`/`<Select.Item>` cannot check item values against the select's own
 * type. Calling a factory once ties the two together — a typo in an item value
 * is then a compile error.
 *
 * The generic defaults to `never` so the type argument cannot be forgotten:
 * without it, no item value is assignable and nothing useful compiles.
 *
 * Pattern from Dominik Dorfmeister's
 * {@link https://tkdodo.eu/blog/building-type-safe-compound-components Building Type-Safe Compound Components}.
 *
 * @example
 * ```tsx
 * type Plan = "free" | "pro" | "enterprise";
 * const PlanSelect = createSelect<Plan>();
 *
 * <PlanSelect.Select label="Plan" placeholder="Choose a plan">
 *   <PlanSelect.Item value="free">Free</PlanSelect.Item>
 *   <PlanSelect.Item value="pro">Pro</PlanSelect.Item>
 * </PlanSelect.Select>
 * ```
 */
export const createSelect = <TValue extends SelectValue = never>(): {
  Select: (props: SelectProps<TValue>) => ReactElement
  Item: (props: SelectItemProps<TValue>) => ReactElement
} => ({ Select: SelectImpl, Item: SelectItemImpl })
